import moment from "moment";
import { IIbptImport } from "src/app/interface/ibpt-import";

/** `tipo` column of the IBPT file */
export const IBPT_TYPE = {
  NCM: 0,
  NBS: 1,
  LC116: 2,
} as const;

const CODE_LENGTH_BY_TYPE: Record<number, number> = {
  [IBPT_TYPE.NCM]: 8,
  [IBPT_TYPE.NBS]: 9,
};

/** Excel serial date 0 (1899-12-30), used when a spreadsheet cell holds a date number */
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export class IbptImportRowError extends Error {
  constructor(public readonly line: number, public readonly reason: string) {
    super(`Linha ${line}: ${reason}`);
  }
}

/**
 * Digits-only code, restoring the leading zeros a spreadsheet reader drops from numeric cells
 * (NCM has 8 digits, NBS 9).
 */
export function normalizeIbptCode(code: unknown, type: number): string {
  const digits = String(code ?? "").replace(/\D/g, "");
  if (!digits) return "";
  const length = CODE_LENGTH_BY_TYPE[type];
  return length ? digits.padStart(length, "0") : digits;
}

/** Accepts `DD/MM/YYYY` text, a `Date` or an Excel serial number; anything else is invalid. */
export function parseIbptDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const utc = new Date(EXCEL_EPOCH_MS + Math.round(value) * ONE_DAY_MS);
    return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
  }

  if (typeof value === "string") {
    const parsed = moment(value.trim(), "DD/MM/YYYY", true);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  return null;
}

export class ItemToBeImported implements IIbptImport {
  code: string;
  ex: number;
  type: number;
  description: string;
  nationalFederal: number;
  importedFederal: number;
  state: number;
  municipal: number;
  startValidity: Date;
  endValidity: Date;
  key: string;

  /**
   * @param data one row of the IBPT file
   * @param line 1-based row number, used in error messages
   */
  constructor(
    data: {
      codigo: string | number;
      ex: number;
      tipo: number | string;
      descricao: string;
      nacionalfederal: number | string;
      importadosfederal: number | string;
      estadual: number | string;
      municipal: number | string;
      vigenciainicio: any;
      vigenciafim: any;
      chave: string;
    },
    line: number,
  ) {
    this.type = Number(data['tipo']);
    this.code = normalizeIbptCode(data['codigo'], this.type);
    if (!this.code) {
      throw new IbptImportRowError(line, "código não informado");
    }

    this.ex = data['ex'];
    this.description = data['descricao'];
    this.nationalFederal = Number(data['nacionalfederal']);
    this.importedFederal = Number(data['importadosfederal']);
    this.state = Number(data['estadual']);
    this.municipal = Number(data['municipal']);
    this.key = data['chave'];

    this.startValidity = parseIbptDate(data['vigenciainicio']);
    this.endValidity = parseIbptDate(data['vigenciafim']);
    if (!this.startValidity || !this.endValidity) {
      throw new IbptImportRowError(line, `data de vigência inválida (código ${this.code})`);
    }
  }
}
